// Admin — Academy Series manager. Create series, attach Bunny videos,
// set featured-hero, manage categories.
import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Star, ChevronDown, Video, Loader2, X, Download, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { adminApi } from "@/lib/adminApi";

const CATEGORIES = [
  { slug: "wine_insights", label: "Wine insights" },
  { slug: "guides", label: "Guides" },
  { slug: "get_started", label: "Get started" },
  { slug: "for_professionals", label: "For professionals" },
];

const AdminAcademySeriesPage = () => {
  const [series, setSeries] = useState([]);
  const [videos, setVideos] = useState([]);
  const [featuredSlug, setFeaturedSlug] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newCategory, setNewCategory] = useState("wine_insights");
  const [importing, setImporting] = useState(false);

  const importFromBunny = async () => {
    if (!window.confirm("Récupérer toutes les Collections Bunny.net et créer/mettre à jour les séries Academy ?\nLes nouvelles séries seront créées en brouillon (à publier manuellement après édition).")) return;
    setImporting(true); setError(null);
    try {
      const r = await adminApi.academyImportFromBunny();
      alert(`✓ Import terminé\n${r.collections_found} Collection(s) trouvée(s)\n${r.created} créée(s) (brouillon)\n${r.updated} mise(s) à jour\n${r.videos_total} vidéo(s) synchronisée(s)${r.errors?.length ? `\n⚠️ ${r.errors.length} erreur(s)` : ""}`);
      await load();
    } catch (e) {
      setError(e.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, v, f] = await Promise.all([
        adminApi.adminListSeries(),
        adminApi.bunnyVideos(),
        adminApi.adminGetFeatured(),
      ]);
      setSeries(s?.items || []);
      setVideos(v?.items || []);
      setFeaturedSlug(f?.series_slug || "");
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, v, f] = await Promise.all([
          adminApi.adminListSeries(),
          adminApi.bunnyVideos(),
          adminApi.adminGetFeatured(),
        ]);
        if (cancelled) return;
        setSeries(s?.items || []);
        setVideos(v?.items || []);
        setFeaturedSlug(f?.series_slug || "");
      } catch (e) { if (!cancelled) setError(e.message || String(e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const createSeries = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      await adminApi.adminCreateSeries({ title: newTitle.trim(), category: newCategory });
      setNewTitle(""); setNewCategory("wine_insights");
      await load();
    } catch (e) { setError(e.message); }
    finally { setCreating(false); }
  };

  const setFeatured = async (slug) => {
    try { await adminApi.adminSetFeatured(slug); setFeaturedSlug(slug); }
    catch (e) { setError(e.message); }
  };

  const updateSeries = async (slug, patch) => {
    try { await adminApi.adminUpdateSeries(slug, patch); await load(); }
    catch (e) { setError(e.message); }
  };

  const deleteSeries = async (slug) => {
    if (!window.confirm(`Supprimer la série "${slug}" ? Les vidéos Bunny ne sont pas supprimées.`)) return;
    try { await adminApi.adminDeleteSeries(slug); await load(); }
    catch (e) { setError(e.message); }
  };

  const attachVideo = async (slug, video) => {
    try {
      await adminApi.adminAttachVideo(slug, { guid: video.guid, title: video.title, length: video.length || 0 });
      await load();
    } catch (e) { setError(e.message); }
  };

  const detachVideo = async (slug, guid) => {
    try { await adminApi.adminDetachVideo(slug, guid); await load(); }
    catch (e) { setError(e.message); }
  };

  // Persist a new video ordering (called by VideosSection after a drag).
  // We optimistically update local state so the UI feels instant, then sync
  // with the backend. On failure we surface the error and reload to revert.
  const reorderVideos = async (slug, orderedGuids) => {
    setSeries((prev) => prev.map((s) => {
      if (s.slug !== slug) return s;
      const byGuid = Object.fromEntries((s.videos || []).map((v) => [v.guid, v]));
      const newVideos = orderedGuids
        .map((g, i) => byGuid[g] ? { ...byGuid[g], order: i + 1 } : null)
        .filter(Boolean);
      return { ...s, videos: newVideos };
    }));
    try { await adminApi.adminReorderVideos(slug, orderedGuids); }
    catch (e) { setError(e.message); await load(); }
  };

  if (loading) return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="animate-spin text-[color:var(--gv-pink)]" /></div>;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-academy-series">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Admin · Academy</div>
          <h1 className="font-display text-3xl text-[color:var(--gv-ink)]">Séries</h1>
          <p className="text-[13px] text-[color:var(--gv-ink)]/65 mt-1">{series.length} série{series.length > 1 ? "s" : ""} · {videos.length} vidéo{videos.length > 1 ? "s" : ""} Bunny disponibles</p>
        </div>
        <button onClick={importFromBunny} disabled={importing}
                className="btn-secondary disabled:opacity-50" data-testid="academy-import-from-bunny"
                title="Récupérer les Collections depuis Bunny.net et créer/mettre à jour les séries Academy">
          {importing ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          Retrieve from Bunny
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-[5px] text-[13px]" data-testid="admin-series-error">{error}</div>}

      {/* Create new series */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-4 flex items-center gap-3" data-testid="admin-series-create">
        <input
          placeholder="Nouveau titre de série…"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          className="flex-1 h-10 px-3 rounded-[5px] border border-[color:var(--gv-line)] text-[13.5px]"
          data-testid="admin-series-new-title"
        />
        <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)} className="h-10 px-2 rounded-[5px] border border-[color:var(--gv-line)] text-[13px]" data-testid="admin-series-new-category">
          {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
        </select>
        <button onClick={createSeries} disabled={creating || !newTitle.trim()} className="btn-primary" data-testid="admin-series-create-btn">
          {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Créer
        </button>
      </div>

      {/* Series list */}
      <div className="space-y-3">
        {series.map((s) => {
          const isOpen = expanded === s.slug;
          const isFeatured = featuredSlug === s.slug;
          return (
            <div key={s.slug} className="bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden" data-testid={`admin-series-${s.slug}`}>
              <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(isOpen ? null : s.slug)}>
                <ChevronDown size={16} className={`text-[color:var(--gv-ink)]/40 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display text-[15px] text-[color:var(--gv-ink)] truncate">{s.title}</div>
                    {isFeatured && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[color:var(--gv-pink)]"><Star size={10} fill="currentColor" /> Featured</span>}
                    {!s.published && <span className="text-[10px] uppercase tracking-widest text-amber-600">Draft</span>}
                  </div>
                  <div className="text-[11.5px] text-[color:var(--gv-ink)]/55">{CATEGORIES.find((c) => c.slug === s.category)?.label || s.category} · {(s.videos || []).length} vidéos</div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setFeatured(isFeatured ? "" : s.slug); }} className={`p-1.5 rounded ${isFeatured ? "text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]/30 hover:text-[color:var(--gv-pink)]"}`} title="Set featured" data-testid={`admin-series-${s.slug}-featured`}>
                  <Star size={15} fill={isFeatured ? "currentColor" : "none"} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); deleteSeries(s.slug); }} className="p-1.5 rounded text-red-500/60 hover:text-red-600" title="Delete" data-testid={`admin-series-${s.slug}-delete`}>
                  <Trash2 size={14} />
                </button>
              </div>
              {isOpen && (
                <div className="border-t border-[color:var(--gv-line)] p-4 bg-[color:var(--gv-mist)]/30 space-y-4" data-testid={`admin-series-${s.slug}-expanded`}>
                  <SeriesEditForm s={s} onSave={(patch) => updateSeries(s.slug, patch)} />
                  <VideosSection s={s} videos={videos} onAttach={(v) => attachVideo(s.slug, v)} onDetach={(g) => detachVideo(s.slug, g)} onReorder={(guids) => reorderVideos(s.slug, guids)} />
                </div>
              )}
            </div>
          );
        })}
        {series.length === 0 && <div className="text-center py-12 text-[color:var(--gv-ink)]/55">Aucune série. Créez-en une ci-dessus.</div>}
      </div>
    </div>
  );
};

const SeriesEditForm = ({ s, onSave }) => {
  const [form, setForm] = useState({ title: s.title, description: s.description || "", category: s.category, hero_image_url: s.hero_image_url || "", level: s.level || "beginner", language: s.language || "fr", published: s.published !== false, sort_order: s.sort_order || 0 });
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); try { await onSave(form); } finally { setSaving(false); } };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`admin-series-${s.slug}-edit-form`}>
      <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Titre" className="gv-input" />
      <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="gv-input">{CATEGORIES.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}</select>
      <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Description" className="gv-input min-h-[80px] md:col-span-2" />
      <input value={form.hero_image_url} onChange={(e) => setForm({...form, hero_image_url: e.target.value})} placeholder="URL image hero (optionnel)" className="gv-input md:col-span-2" />
      <select value={form.level} onChange={(e) => setForm({...form, level: e.target.value})} className="gv-input"><option value="beginner">Débutant</option><option value="intermediate">Intermédiaire</option><option value="advanced">Avancé</option></select>
      <input type="number" value={form.sort_order} onChange={(e) => setForm({...form, sort_order: parseInt(e.target.value || "0", 10)})} placeholder="Ordre" className="gv-input" />
      <label className="flex items-center gap-2 text-[13px] md:col-span-2"><input type="checkbox" checked={form.published} onChange={(e) => setForm({...form, published: e.target.checked})} /> Publiée (visible côté public)</label>
      <button onClick={save} disabled={saving} className="btn-primary md:col-span-2 justify-center" data-testid={`admin-series-${s.slug}-save`}>
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Sauvegarder
      </button>
    </div>
  );
};

const SortableVideoRow = ({ slug, v, onDetach }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: v.guid });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 bg-white p-2 border border-[color:var(--gv-line)] rounded-[5px] ${isDragging ? "shadow-lg" : ""}`}
      data-testid={`admin-series-${slug}-video-${v.guid}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-[color:var(--gv-ink)]/30 hover:text-[color:var(--gv-ink)]/70 p-1 touch-none"
        title="Glisser pour réordonner"
        aria-label="Réordonner cette vidéo"
        data-testid={`admin-series-${slug}-video-${v.guid}-handle`}
      >
        <GripVertical size={14} />
      </button>
      <Video size={14} className="text-[color:var(--gv-ink)]/40" />
      <div className="flex-1 text-[13px] truncate">{v.title || "(sans titre)"}</div>
      <div className="text-[11px] text-[color:var(--gv-ink)]/55">#{v.order || "?"}</div>
      <button onClick={() => onDetach(v.guid)} className="text-red-500/60 hover:text-red-600 p-1" title="Détacher" data-testid={`admin-series-${slug}-video-${v.guid}-detach`}>
        <X size={14} />
      </button>
    </div>
  );
};

const VideosSection = ({ s, videos, onAttach, onDetach, onReorder }) => {
  const attached = (s.videos || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const attachedGuids = new Set(attached.map((v) => v.guid));
  const available = videos.filter((v) => !attachedGuids.has(v.guid));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = attached.findIndex((v) => v.guid === active.id);
    const newIndex = attached.findIndex((v) => v.guid === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(attached, oldIndex, newIndex).map((v) => v.guid);
    onReorder(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55">Vidéos attachées ({attached.length})</div>
        {attached.length > 1 && (
          <div className="text-[10.5px] text-[color:var(--gv-ink)]/45">Glissez <GripVertical size={11} className="inline -mt-0.5" /> pour réordonner</div>
        )}
      </div>
      {attached.length > 0 ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={attached.map((v) => v.guid)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2" data-testid={`admin-series-${s.slug}-videos-list`}>
              {attached.map((v) => (
                <SortableVideoRow key={v.guid} slug={s.slug} v={v} onDetach={onDetach} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="text-[12px] text-[color:var(--gv-ink)]/45 italic">Aucune vidéo attachée pour l&apos;instant.</div>
      )}
      {available.length > 0 && (
        <details className="bg-white border border-[color:var(--gv-line)] rounded-[5px]">
          <summary className="cursor-pointer px-3 py-2 text-[12.5px] text-[color:var(--gv-ink)]/70">+ Ajouter une vidéo Bunny ({available.length} disponibles)</summary>
          <div className="max-h-60 overflow-y-auto p-2 space-y-1">
            {available.map((v) => (
              <button key={v.guid} onClick={() => onAttach(v)} className="w-full flex items-center gap-2 p-2 hover:bg-[color:var(--gv-mist)] rounded text-left text-[12.5px]" data-testid={`admin-series-attach-${v.guid}`}>
                <Plus size={12} className="text-[color:var(--gv-pink)]" />
                <span className="flex-1 truncate">{v.title || v.guid}</span>
                <span className="text-[10px] text-[color:var(--gv-ink)]/40">{v.length ? `${Math.round(v.length / 60)}min` : ""}</span>
              </button>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default AdminAcademySeriesPage;
